import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import resourceService from '../services/resourceService';
import resourceEnrollmentService from '../services/resourceEnrollmentService';
import paymentService from '../services/paymentService';

// Icons
const LockIcon = () => (
  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
);
const UnlockIcon = () => (
  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
);
const DocumentIcon = () => (
  <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);

const ResourceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [resource, setResource] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  
  // Preview Modal State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // 1. Fetch Resource Details
        const resourceData = await resourceService.getResourceById(id);
        setResource(resourceData);

        // 2. Check Enrollment (if logged in)
        if (isAuthenticated) {
          const enrolled = await resourceEnrollmentService.checkResourceEnrollment(id);
          setIsEnrolled(enrolled);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load resource details.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isAuthenticated]);

  const handleBuy = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      setPurchasing(true);
      const result = await paymentService.processResourcePayment(resource, user);
      if (result.success) {
        setIsEnrolled(true);
        alert('Purchase successful!');
      }
    } catch (err) {
      alert(err || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleAccess = async () => {
    try {
      const data = await resourceService.getResourceUrl(resource.id);
      if (data && data.url) {
        setPreviewUrl(data.url);
        setPreviewOpen(true);
      } else {
        alert('Could not retrieve access URL.');
      }
    } catch (err) {
      alert('Failed to access content.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
          <p className="text-slate-600">{error || 'Resource not found'}</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-primary-600 hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  const isMockTest = resource.type === 'MOCK_TESTS';
  const typeLabel = isMockTest ? 'Mock Test' : 'Study Note';
  
  // Logic to handle free resources
  const isFree = resource.price === 0;
  // User has access if they are enrolled OR the resource is free
  const canAccess = isEnrolled || isFree;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Background */}
      <div className="bg-slate-900 h-64 w-full absolute top-0 left-0 z-0"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        
        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 flex flex-col md:flex-row">
          
          {/* Left: Thumbnail */}
          <div className="md:w-2/5 h-64 md:h-auto bg-slate-100 relative">
            <img 
              src={resource.thumbnailUrl || 'https://placehold.co/600x800/e2e8f0/475569?text=Resource'} 
              alt={resource.title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-800 shadow-sm">
              {resource.subject || 'General'}
            </div>
          </div>

          {/* Right: Details */}
          <div className="md:w-3/5 p-8 md:p-10 flex flex-col">
            <div className="mb-auto">
              <div className="flex items-center space-x-2 text-sm font-medium text-slate-500 mb-3">
                <span className="uppercase tracking-wider">{typeLabel}</span>
                <span>•</span>
                <span>Created {new Date(resource.createdAt).toLocaleDateString()}</span>
              </div>
              
              <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4 leading-tight">
                {resource.title}
              </h1>
              
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {resource.description || "No description available for this resource."}
              </p>
            </div>

            {/* Action Area */}
            <div className="border-t border-slate-100 pt-8 mt-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Price</p>
                  <p className={`text-3xl font-bold ${isFree ? 'text-green-600' : 'text-slate-900'}`}>
                    {isFree ? 'Free' : `₹${resource.price}`}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  {canAccess ? (
                    <button 
                      onClick={handleAccess}
                      className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all shadow-lg hover:shadow-green-500/30"
                    >
                      <UnlockIcon />
                      {isMockTest ? 'Start Test' : 'View PDF'}
                    </button>
                  ) : (
                    <button 
                      onClick={handleBuy}
                      disabled={purchasing}
                      className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-slate-900 rounded-xl hover:bg-slate-800 transition-all shadow-lg hover:shadow-slate-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {purchasing ? (
                        <>Processing...</>
                      ) : (
                        <>
                          <LockIcon />
                          Unlock Now
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Instant Access</h3>
            <p className="text-sm text-slate-600">Start learning immediately after purchase.</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600 mb-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
            </div>
            <h3 className="font-bold text-slate-900 mb-2">High Quality</h3>
            <p className="text-sm text-slate-600">Curated by experts for best results.</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 mb-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Verified Content</h3>
            <p className="text-sm text-slate-600">Up-to-date with latest syllabus.</p>
          </div>
        </div>

      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h3 className="font-semibold text-slate-900 flex items-center">
                {isMockTest ? '📝 Test Interface' : '📄 PDF Viewer'}
                <span className="mx-2 text-slate-300">|</span>
                <span className="font-normal text-slate-600">{resource.title}</span>
              </h3>
              <button 
                onClick={() => setPreviewOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500 hover:text-slate-800"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 bg-slate-100 relative">
               <iframe 
                  src={previewUrl} 
                  title="Content Preview"
                  className="w-full h-full border-none"
               />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceDetailPage;